if (!customElements.get('rbs-video-hero')) {
  class RbsVideoHero extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      this.poster = this.querySelector('.rbs-video-hero__poster img');
      if (!this.video) return;

      if (navigator.connection?.saveData) {
        this.showSharpPoster();
        return;
      }

      this.video.addEventListener('error', () => this.onError());
      this.video.addEventListener('playing', () => this.onFirstFrame(), { once: true });

      // Only the visitor may stop it, not the browser or pauseAllMedia. rAF waits for a visible page.
      this.video.addEventListener('pause', () => {
        if (this.shouldPlay()) requestAnimationFrame(() => this.shouldPlay() && this.play());
      });
      this.onVisibilityChange = () => this.shouldPlay() && this.play();
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      this.querySelector('.js-pause')?.addEventListener('click', (e) => this.togglePause(e.currentTarget));
      this.querySelector('.js-sound')?.addEventListener('click', (e) => this.toggleSound(e.currentTarget));

      // The video starts once the placeholder has loaded.
      const posterLoaded = !this.poster || this.poster.complete ? null : new Promise((resolve) => {
        this.poster.addEventListener('load', resolve, { once: true });
        this.poster.addEventListener('error', resolve, { once: true });
      });
      Promise.all([this.chooseSource(), posterLoaded]).then(() => this.observe());
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.interaction?.abort();
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }

    /**
     * Sets the MP4 that matches the quality for this screen. Liquid only knows the tallest MP4;
     * the others are listed in Shopify's HLS playlist, with the bitrate in their names.
     */
    async chooseSource() {
      const size = window.matchMedia('(max-width: 767.98px)').matches ? 'Mobile' : 'Desktop';
      const { [`src${size}`]: fallback, [`playlist${size}`]: playlist, [`quality${size}`]: quality } = this.video.dataset;
      this.fallbackSrc = new URL(fallback, window.location.href).href;

      let src = this.fallbackSrc;
      if (playlist) {
        try {
          const response = await fetch(playlist, { signal: AbortSignal.timeout?.(3000) });
          src = RbsVideoHero.pickRendition(await response.text(), new URL(playlist, window.location.href), +quality) || src;
        } catch {
          // Keep the tallest MP4.
        }
      }
      this.video.src = src;
    }

    /**
     * Tallest rendition up to the quality, or the smallest when all are taller.
     * 'x.HD-720p.3.0Mbps.hls.123_.m3u8' in the playlist is 'x.HD-720p-3.0Mbps-123.mp4' on the CDN.
     */
    static pickRendition(text, base, quality) {
      const lines = text.split('\n');
      const renditions = [];
      lines.forEach((line, i) => {
        const height = +line.match(/RESOLUTION=\d+x(\d+)/)?.[1];
        const name = lines[i + 1]?.trim().replace(/\.((?:HD|SD)-\d+p)\.([\d.]+Mbps)\.hls\.(\d+)_\.m3u8$/, '.$1-$2-$3.mp4');
        if (height && name?.endsWith('.mp4')) renditions.push({ height, url: new URL(name, base).href });
      });
      renditions.sort((a, b) => a.height - b.height);
      return (renditions.filter((r) => r.height <= quality).pop() || renditions[0])?.url;
    }

    onError() {
      if (this.video.src !== this.fallbackSrc) {
        this.video.src = this.fallbackSrc;
        if (this.inView && !this.pausedByUser) this.play();
      } else {
        this.showSharpPoster();
      }
    }

    observe() {
      this.observer = new IntersectionObserver(([entry]) => {
        this.inView = entry.isIntersecting;
        if (this.shouldPlay()) {
          this.play();
        } else if (!this.inView && !this.video.paused) {
          this.video.pause();
        }
      }, { rootMargin: '200px 0px' });
      this.observer.observe(this);
    }

    shouldPlay() {
      return this.inView && !this.pausedByUser && !document.hidden && this.video.paused;
    }

    play() {
      // Sharp placeholder if the video has not started within 4 s.
      this.slowTimer ??= setTimeout(() => this.showSharpPoster(), 4000);
      this.video.preload = 'auto';
      this.video.play().catch((err) => {
        // Autoplay refused, for example in Low Power Mode: start on the first tap, click or key.
        if (err.name !== 'NotAllowedError') return;
        this.showSharpPoster();
        this.playOnInteraction();
      });
    }

    playOnInteraction() {
      if (this.interaction) return;
      this.interaction = new AbortController();
      const start = () => {
        this.interaction.abort();
        this.interaction = null;
        if (this.inView && !this.pausedByUser) this.play();
      };
      ['click', 'touchend', 'keydown'].forEach((type) => {
        document.addEventListener(type, start, { signal: this.interaction.signal, passive: true });
      });
    }

    onFirstFrame() {
      // 'playing' can fire before a frame is on screen; wait for the frame itself.
      const reveal = () => {
        clearTimeout(this.slowTimer);
        this.classList.add('is-playing');
      };
      if ('requestVideoFrameCallback' in this.video) {
        this.video.requestVideoFrameCallback(reveal);
      } else {
        reveal();
      }
    }

    showSharpPoster() {
      if (this.classList.contains('is-playing')) return;
      this.classList.add('is-still');
      this.querySelectorAll('.rbs-video-hero__poster [data-sharp-srcset]').forEach((el) => {
        el.srcset = el.dataset.sharpSrcset;
      });
    }

    togglePause(button) {
      this.pausedByUser = !this.video.paused;
      if (this.pausedByUser) {
        this.video.pause();
      } else {
        this.play();
      }
      button.setAttribute('aria-pressed', this.pausedByUser);
    }

    toggleSound(button) {
      this.video.muted = !this.video.muted;
      button.setAttribute('aria-pressed', !this.video.muted);
    }
  }

  customElements.define('rbs-video-hero', RbsVideoHero);
}
